import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';
import {
  Treatment,
  TreatmentSet,
  TreatmentInfo,
} from '../../types/treatment.types';

// Paginated response from API
interface PagedResponse<T> {
  current_page: number;
  total_pages: number;
  total_items: number;
  page_size: number;
  items: T[];
}

interface TreatmentsContextType {
  // Cached treatment sets by disease ID
  treatmentSets: Map<number, TreatmentSet[]>;
  // Cached treatments by disease ID and treatment set ID
  treatments: Map<string, Treatment[]>; // key: `${diseaseId}-${treatmentSetId}`
  // Loading states
  loadingTreatmentSets: Set<number>;
  loadingTreatments: Set<string>;
  // Error state
  error: string | null;

  // Methods
  fetchTreatmentSets: (diseaseId: number) => Promise<TreatmentSet[]>;
  fetchTreatments: (
    diseaseId: number,
    treatmentSetId: number
  ) => Promise<Treatment[]>;
  getTreatmentSets: (diseaseId: number) => TreatmentSet[] | null;
  getTreatments: (
    diseaseId: number,
    treatmentSetId: number
  ) => Treatment[] | null;
  isLoadingTreatmentSets: (diseaseId: number) => boolean;
  isLoadingTreatments: (diseaseId: number, treatmentSetId: number) => boolean;
  clearError: () => void;

  // Fetch all treatment info for a disease (sets + treatments for each set)
  fetchAllTreatmentInfo: (diseaseId: number) => Promise<TreatmentInfo | null>;
}

const TreatmentsContext = createContext<TreatmentsContextType | undefined>(
  undefined
);

interface TreatmentsProviderProps {
  children: ReactNode;
}

export const TreatmentsProvider: React.FC<TreatmentsProviderProps> = ({
  children,
}) => {
  const [treatmentSets, setTreatmentSets] = useState<
    Map<number, TreatmentSet[]>
  >(new Map());
  const [treatments, setTreatments] = useState<Map<string, Treatment[]>>(
    new Map()
  );
  const [loadingTreatmentSets, setLoadingTreatmentSets] = useState<Set<number>>(
    new Set()
  );
  const [loadingTreatments, setLoadingTreatments] = useState<Set<string>>(
    new Set()
  );
  const [error, setError] = useState<string | null>(null);

  const api = useMemo(() => gekkoApi, []);

  // Generate cache key for treatments
  const getTreatmentKey = useCallback(
    (diseaseId: number, treatmentSetId: number) =>
      `${diseaseId}-${treatmentSetId}`,
    []
  );

  // Fetch treatment sets (protocols) for a disease
  const fetchTreatmentSets = useCallback(
    async (diseaseId: number): Promise<TreatmentSet[]> => {
      // Return cached if available
      if (treatmentSets.has(diseaseId)) {
        return treatmentSets.get(diseaseId) || [];
      }

      // Don't fetch if already loading
      if (loadingTreatmentSets.has(diseaseId)) {
        return [];
      }

      try {
        setLoadingTreatmentSets((prev) => new Set([...prev, diseaseId]));
        setError(null);

        const response = await api.get<PagedResponse<TreatmentSet>>(
          `/diseases/${diseaseId}/treatment-protocols`
        );

        const sets = response.items || [];

        // Sort by order
        sets.sort((a, b) => a.order - b.order);

        setTreatmentSets((prev) => new Map([...prev, [diseaseId, sets]]));
        return sets;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch treatment sets';
        setError(errorMessage);
        console.error('Error fetching treatment sets:', err);
        return [];
      } finally {
        setLoadingTreatmentSets((prev) => {
          const newSet = new Set(prev);
          newSet.delete(diseaseId);
          return newSet;
        });
      }
    },
    [treatmentSets, loadingTreatmentSets, api]
  );

  // Fetch treatments for a specific disease and treatment set
  const fetchTreatments = useCallback(
    async (diseaseId: number, treatmentSetId: number): Promise<Treatment[]> => {
      const key = getTreatmentKey(diseaseId, treatmentSetId);

      // Return cached if available
      if (treatments.has(key)) {
        return treatments.get(key) || [];
      }

      // Don't fetch if already loading
      if (loadingTreatments.has(key)) {
        return [];
      }

      try {
        setLoadingTreatments((prev) => new Set([...prev, key]));
        setError(null);

        const response = await api.get<PagedResponse<Treatment>>(
          `/diseases/${diseaseId}/treatment-protocols/${treatmentSetId}/treatments`
        );

        const treatmentList = response.items || [];

        setTreatments((prev) => new Map([...prev, [key, treatmentList]]));
        return treatmentList;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch treatments';
        setError(errorMessage);
        console.error('Error fetching treatments:', err);
        return [];
      } finally {
        setLoadingTreatments((prev) => {
          const newSet = new Set(prev);
          newSet.delete(key);
          return newSet;
        });
      }
    },
    [treatments, loadingTreatments, api, getTreatmentKey]
  );

  // Get cached treatment sets
  const getTreatmentSets = useCallback(
    (diseaseId: number): TreatmentSet[] | null => {
      return treatmentSets.get(diseaseId) || null;
    },
    [treatmentSets]
  );

  // Get cached treatments
  const getTreatments = useCallback(
    (diseaseId: number, treatmentSetId: number): Treatment[] | null => {
      const key = getTreatmentKey(diseaseId, treatmentSetId);
      return treatments.get(key) || null;
    },
    [treatments, getTreatmentKey]
  );

  // Check if loading treatment sets
  const isLoadingTreatmentSets = useCallback(
    (diseaseId: number): boolean => {
      return loadingTreatmentSets.has(diseaseId);
    },
    [loadingTreatmentSets]
  );

  // Check if loading treatments
  const isLoadingTreatments = useCallback(
    (diseaseId: number, treatmentSetId: number): boolean => {
      const key = getTreatmentKey(diseaseId, treatmentSetId);
      return loadingTreatments.has(key);
    },
    [loadingTreatments, getTreatmentKey]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch all treatment info for a disease
  const fetchAllTreatmentInfo = useCallback(
    async (diseaseId: number): Promise<TreatmentInfo | null> => {
      try {
        // First fetch treatment sets
        const sets = await fetchTreatmentSets(diseaseId);

        if (!sets || sets.length === 0) {
          return null;
        }

        // Fetch treatments for each set in parallel
        const treatmentPromises = sets.map((set) =>
          fetchTreatments(diseaseId, set.id).then((treatmentList) => ({
            setId: set.id,
            treatments: treatmentList,
          }))
        );

        const results = await Promise.all(treatmentPromises);

        // Build treatments map
        const treatmentsMap = new Map<number, Treatment[]>();
        results.forEach(({ setId, treatments: treatmentList }) => {
          treatmentsMap.set(setId, treatmentList);
        });

        return {
          diseaseId,
          diseaseName: '', // Will be filled by caller
          treatmentSets: sets,
          treatments: treatmentsMap,
        };
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch treatment info';
        setError(errorMessage);
        console.error('Error fetching all treatment info:', err);
        return null;
      }
    },
    [fetchTreatmentSets, fetchTreatments]
  );

  const value: TreatmentsContextType = {
    treatmentSets,
    treatments,
    loadingTreatmentSets,
    loadingTreatments,
    error,
    fetchTreatmentSets,
    fetchTreatments,
    getTreatmentSets,
    getTreatments,
    isLoadingTreatmentSets,
    isLoadingTreatments,
    clearError,
    fetchAllTreatmentInfo,
  };

  return (
    <TreatmentsContext.Provider value={value}>
      {children}
    </TreatmentsContext.Provider>
  );
};

export const useTreatments = (): TreatmentsContextType => {
  const context = useContext(TreatmentsContext);
  if (context === undefined) {
    throw new Error('useTreatments must be used within a TreatmentsProvider');
  }
  return context;
};

// Export types
export type { Treatment, TreatmentSet, TreatmentInfo };
